import React from "react";
import { Users, Wallet, Percent, TrendingUp, LifeBuoy, Activity } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, AreaChart, Area } from "recharts";
import { eurf, Kpi, Panel, tipStyle, STATUS_LABELS, PIE_COLORS } from "./shared";

export default function Overview({ stats }) {
  if (!stats) return <p className="text-zinc-500" data-testid="sa-overview-loading">Chargement des statistiques…</p>;
  const pie = (stats.subscriptions?.by_status || []).map((s) => ({ ...s, label: STATUS_LABELS[s.status] || s.status }));
  const revComp = [
    { name: "Abonnements", value: stats.subscription_revenue },
    { name: "Commissions", value: stats.commission_revenue },
  ];
  return (
    <div className="space-y-6" data-testid="sa-overview">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
        <div data-testid="sa-active-users" className="relative overflow-hidden rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.06] p-5">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
            </span>
            <Activity className="h-4 w-4 text-emerald-400" strokeWidth={1.8} />
            <span className="text-sm font-medium text-emerald-200">Utilisateurs actifs en ce moment</span>
          </div>
          <p className="mt-3 text-4xl font-bold text-white">{stats.active_users ?? 0}</p>
          <p className="mt-1 text-xs text-emerald-200/70">connectés dans les 5 dernières minutes — évitez une mise à jour s'il y a du monde</p>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <Kpi icon={Users} label="Abonnements actifs" value={stats.subscriptions?.active ?? 0} sub={`${stats.subscriptions?.total_orgs ?? 0} prestataires au total`} tint="#FF6A00" />
        <Kpi icon={Wallet} label="Recettes abonnements" value={eurf(stats.subscription_revenue)} sub="par mois (MRR)" tint="#FFD500" />
        <Kpi icon={TrendingUp} label="MRR prévisionnel" value={eurf(stats.forecast_revenue)} sub="actifs + essais convertis" tint="#a78bfa" />
        <Kpi icon={Percent} label="Recettes commissions" value={eurf(stats.commission_revenue)} sub="sur le CA confirmé" tint="#34d399" />
        <Kpi icon={LifeBuoy} label="Demandes support" value={stats.support?.open ?? 0} sub={`${stats.support?.resolved ?? 0} traitées`} tint="#f87171" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="Recettes mensuelles par plan">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.revenue_by_plan} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
              <XAxis dataKey="plan" tick={{ fill: "#a1a1aa", fontSize: 12 }} axisLine={{ stroke: "#3f3f46" }} tickLine={false} />
              <YAxis tick={{ fill: "#a1a1aa", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tipStyle} cursor={{ fill: "#ffffff08" }} formatter={(v) => eurf(v)} />
              <Bar dataKey="revenue" name="Recettes" fill="#FF6A00" radius={[6, 6, 0, 0]} maxBarSize={64} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Répartition des abonnements">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={pie} dataKey="count" nameKey="label" cx="50%" cy="50%" innerRadius={58} outerRadius={92} paddingAngle={3} stroke="none">
                {pie.map((e) => <Cell key={e.status} fill={PIE_COLORS[e.status] || "#52525b"} />)}
              </Pie>
              <Tooltip contentStyle={tipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 flex flex-wrap justify-center gap-3">
            {pie.map((e) => (
              <span key={e.status} className="flex items-center gap-1.5 text-xs text-zinc-400">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[e.status] || "#52525b" }} />{e.label} · {e.count}
              </span>
            ))}
          </div>
        </Panel>

        <Panel title="Recettes : abonnements vs commissions">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={revComp} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: "#a1a1aa", fontSize: 12 }} axisLine={{ stroke: "#3f3f46" }} tickLine={false} />
              <YAxis tick={{ fill: "#a1a1aa", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tipStyle} cursor={{ fill: "#ffffff08" }} formatter={(v) => eurf(v)} />
              <Bar dataKey="value" name="Recettes" radius={[6, 6, 0, 0]} maxBarSize={80}>
                <Cell fill="#FFD500" /><Cell fill="#34d399" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Demandes de support (6 derniers mois)">
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={stats.ticket_series} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="tkg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF6A00" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#FF6A00" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: "#a1a1aa", fontSize: 12 }} axisLine={{ stroke: "#3f3f46" }} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: "#a1a1aa", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tipStyle} cursor={{ stroke: "#3f3f46" }} />
              <Area type="monotone" dataKey="count" name="Tickets" stroke="#FF6A00" strokeWidth={2} fill="url(#tkg)" />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>

        <div className="lg:col-span-2">
          <Panel title="Top prestataires par chiffre d'affaires">
            {(!stats.top_orgs || stats.top_orgs.length === 0) ? (
              <p className="text-sm text-zinc-500">Aucune donnée de CA pour le moment.</p>
            ) : (
              <div className="space-y-3" data-testid="sa-top-orgs">
                {stats.top_orgs.map((o, i) => {
                  const max = stats.top_orgs[0]?.revenue || 1;
                  const pct = Math.max(4, Math.round((o.revenue / max) * 100));
                  return (
                    <div key={`${o.name}-${i}`} className="flex items-center gap-3">
                      <span className="w-5 text-center text-sm font-bold text-zinc-500">{i + 1}</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-zinc-200">{o.name}</span>
                          <span className="font-semibold text-white">{eurf(o.revenue)}</span>
                        </div>
                        <div className="mt-1 h-2 rounded-full bg-zinc-800">
                          <div className="h-2 rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#FF6A00,#FFD500)" }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
